import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Mic, ShieldCheck, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

/* ── Dati: timeline (una riga per tappa, niente architettura interna) ───── */
const STEPS = [
  {
    stamp: "0:00",
    n: "1",
    title: "Incolli il tuo indirizzo",
    text:
      "Il sito della tua attività, o anche solo il profilo Instagram o la pagina Google. Basta un link pubblico: niente accessi, niente installazioni.",
  },
  {
    stamp: "0:10",
    n: "2",
    title: "Il sistema legge chi sei",
    text:
      "Analizza le tue pagine pubbliche e costruisce l'identità della tua attività — cosa offri, come parli, gli orari e le informazioni pratiche. Da solo, senza questionari.",
  },
  {
    stamp: "2:00",
    n: "3",
    title: "Nasce la tua voce",
    text:
      "Un assistente vocale su misura: una voce italiana naturale che risponde alle domande tipiche dei tuoi clienti. Conversa, non recita.",
  },
  {
    stamp: "5:00",
    n: "4",
    title: "Sei operativa",
    text:
      "Ricevi il numero da collegare alla tua linea. Da questo momento ogni chiamata che non puoi prendere trova una voce che risponde, 24 ore su 24.",
  },
  {
    stamp: "→",
    n: "5",
    final: true,
    title: "Ogni chiamata, sotto controllo",
    text:
      "Ricevi l'esito di ogni conversazione su Telegram nell'istante in cui succede. Sai sempre chi ha chiamato, cosa voleva e com'è andata.",
  },
];

/* ── Dati: le tre parti (qui vivono i nomi + i \"Cosa non fa\") ──────────── */
const PARTS = [
  {
    icon: Search,
    name: "Motore di Comprensione",
    role: "Capisce la tua azienda",
    text:
      "Legge il tuo sito e ne ricava attività, offerta, tono di voce e informazioni pratiche. Se il sito cambia, lo rileggi con un messaggio: l'assistente si aggiorna senza interventi tecnici.",
    notDo:
      "Non inventa informazioni che non trova. Se un dato manca, l'assistente lo dichiara e prende il recapito del cliente — non improvvisa.",
  },
  {
    icon: Mic,
    name: "Agente Vocale",
    role: "Parla, ascolta, risponde",
    text:
      "Una voce italiana naturale che sostiene una conversazione vera: gestisce interruzioni e cambi di argomento, reagisce senza attese, fa quello che farebbe una persona al telefono.",
    notDo:
      "Non si spaccia per umana se le viene chiesto, e non insiste: se il cliente vuole parlare con te, raccoglie il messaggio e ti avvisa subito.",
  },
  {
    icon: ShieldCheck,
    name: "Sistema di Controllo",
    role: "Vigila, sempre",
    text:
      "Monitora ogni chiamata 24 ore su 24, registra l'esito e ti notifica su Telegram nell'istante in cui qualcosa succede. Storico e controllo restano sempre nelle tue mani.",
    notDo:
      "Non decide al posto tuo. Segnala, riporta e archivia: le scelte sul tuo business restano dove devono stare.",
  },
];

/* ── Dati: FAQ \"E se…?\" (4 domande) ────────────────────────────────────── */
const FAQ = [
  {
    q: "E se l'AI non sa rispondere a una domanda?",
    a: (
      <>
        Lo dice. Risponde che non ha quell'informazione,{" "}
        <b className="text-brand-ink">prende nome e recapito del cliente</b> e ti gira tutto su
        Telegram come messaggio prioritario. E se un cliente vuole parlare proprio con te, non lo
        trattiene: raccoglie nome, motivo e recapito e ti avvisa subito, così puoi richiamare appena
        puoi. Nel peggiore dei casi hai un messaggio dettagliato invece di una chiamata persa.
      </>
    ),
  },
  {
    q: "Posso correggerla se sbaglia o se cambio qualcosa?",
    a: (
      <>
        Sì, con un messaggio Telegram in italiano. «Da lunedì chiudiamo alle 18», «non offriamo più
        il servizio X»:{" "}
        <b className="text-brand-ink">scrivi l'istruzione, l'assistente la applica dalla chiamata successiva.</b>{" "}
        Zero pannelli, zero ticket di assistenza.
      </>
    ),
  },
  {
    q: "Chi sente le mie chiamate? Dove finiscono i dati?",
    a: (
      <>
        Le registrazioni e gli esiti sono visibili <b className="text-brand-ink">solo a te</b>, nel
        tuo storico e nelle tue notifiche. Nessun call center, nessun operatore terzo in ascolto: il
        sistema è automatico end-to-end e i dati dei tuoi clienti restano dati dei tuoi clienti.
      </>
    ),
  },
  {
    q: "Come si consumano le ricariche? Quanto mi costa una chiamata?",
    a: (
      <>
        Le ricariche si consumano <b className="text-brand-ink">a minuti di conversazione effettiva</b>:
        paghi solo il tempo in cui l'assistente è davvero al telefono. Vedi il credito residuo in
        ogni momento da Telegram, e quando scende sotto soglia ti avvisiamo prima — mai
        un'interruzione a sorpresa.
      </>
    ),
  },
];

export default function ComeFunziona() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("in");
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="come-funziona-page" className="bg-brand-bg" id="top">
      <Header />
      <main>
        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <header className="pt-24 pb-16 md:pt-28 md:pb-20">
          <div className="max-w-5xl mx-auto px-6 md:px-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-bg2 border border-brand-line mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-terra" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-olive">
                Come funziona · passo per passo
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.08] tracking-tight max-w-3xl">
              Cosa succede davvero nei{" "}
              <span className="text-brand-terra italic">cinque minuti</span> tra il tuo URL e la
              prima chiamata.
            </h1>
            <p className="mt-6 text-lg text-brand-inkSoft max-w-xl">
              Ogni passaggio, con i tempi veri.{" "}
              <span className="text-brand-ink">E le risposte prima che tu le chieda.</span>
            </p>
          </div>
        </header>

        {/* ── TIMELINE ─────────────────────────────────────────────────── */}
        <section className="py-20 md:py-24" id="timeline">
          <div className="max-w-5xl mx-auto px-6 md:px-8">
            <div className="reveal max-w-2xl">
              <p className="text-xs font-medium tracking-[0.25em] text-brand-olive uppercase mb-4">
                La sequenza
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
                Dal tuo indirizzo alla tua voce. Cronometrata.
              </h2>
            </div>

            <ol className="mt-14 relative">
              {/* linea verticale */}
              <span
                aria-hidden="true"
                className="absolute top-2 bottom-2 left-[68px] md:left-[76px] w-px bg-brand-line hidden sm:block"
              />
              {STEPS.map((s) => (
                <li
                  key={s.n}
                  className="reveal grid grid-cols-[48px_28px_1fr] md:grid-cols-[56px_28px_1fr] gap-x-4 md:gap-x-6 pb-10 last:pb-0 relative"
                >
                  <div className="text-right pt-1 text-sm font-bold tabular-nums text-brand-terra">
                    {s.stamp}
                  </div>
                  <div
                    className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold relative z-10 border-2 border-brand-terra ${
                      s.final ? "bg-brand-terra text-white" : "bg-brand-bg text-brand-terra"
                    }`}
                  >
                    {s.n}
                  </div>
                  <div className="pt-0.5">
                    <h3 className="font-heading text-xl text-brand-ink">{s.title}</h3>
                    <p className="mt-2 text-brand-inkSoft max-w-xl leading-relaxed">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── TRE PARTI ────────────────────────────────────────────────── */}
        <section className="py-20 md:py-24 bg-brand-bg2" id="sistema">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <div className="reveal max-w-2xl">
              <p className="text-xs font-medium tracking-[0.25em] text-brand-olive uppercase mb-4">
                Dentro il sistema
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
                Tre parti. Ognuna fa una cosa sola, e la fa bene.
              </h2>
              <p className="mt-4 text-brand-inkSoft">
                Insieme sostituiscono il lavoro di un intero ufficio. E per ognuna ti diciamo anche
                cosa <i>non</i> fa — perché la fiducia si costruisce sui limiti dichiarati, non sulle
                promesse.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {PARTS.map((p) => (
                <article
                  key={p.name}
                  className="reveal flex flex-col bg-brand-bg border border-brand-line rounded-2xl p-7"
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-bg2 border border-brand-line grid place-items-center mb-6">
                    <p.icon className="w-5 h-5 text-brand-olive" strokeWidth={2} />
                  </div>
                  <h3 className="font-heading text-xl text-brand-ink">{p.name}</h3>
                  <p className="text-xs font-bold tracking-[0.1em] uppercase text-brand-terra mt-1.5">
                    {p.role}
                  </p>
                  <p className="mt-4 text-sm text-brand-inkSoft leading-relaxed flex-1">{p.text}</p>
                  <div className="mt-5 pt-4 border-t border-dashed border-brand-line">
                    <p className="text-[0.7rem] font-bold tracking-[0.14em] uppercase text-brand-ink mb-1.5">
                      Cosa non fa
                    </p>
                    <p className="text-sm text-brand-inkSoft leading-relaxed">{p.notDo}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ \"E se…?\" ─────────────────────────────────────────────── */}
        <section className="py-20 md:py-24" id="e-se">
          <div className="max-w-3xl mx-auto px-6 md:px-8">
            <div className="reveal">
              <p className="text-xs font-medium tracking-[0.25em] text-brand-olive uppercase mb-4">
                Le domande giuste
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
                «E se…?» — le risposte che cercheresti prima di fidarti.
              </h2>
            </div>

            <div className="mt-10 space-y-3.5">
              {FAQ.map((item, i) => (
                <details
                  key={i}
                  className="reveal group bg-brand-bg border border-brand-line rounded-xl open:border-brand-terra overflow-hidden"
                >
                  <summary className="list-none [&::-webkit-details-marker]:hidden cursor-pointer flex items-center justify-between gap-4 px-6 py-5 font-heading text-lg text-brand-ink">
                    {item.q}
                    <span className="shrink-0 w-7 h-7 rounded-full border border-brand-ink grid place-items-center text-lg leading-none transition-transform duration-200 group-open:rotate-45 group-open:bg-brand-terra group-open:border-brand-terra group-open:text-white">
                      +
                    </span>
                  </summary>
                  <div className="px-6 pb-6 -mt-1 text-brand-inkSoft leading-relaxed">{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── TELEGRAM ─────────────────────────────────────────────────── */}
        <section className="py-20 md:py-24 bg-brand-bg2" id="telegram">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="reveal">
                <p className="text-xs font-medium tracking-[0.25em] text-brand-olive uppercase mb-4">
                  Il controllo, ogni giorno
                </p>
                <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight">
                  La gestione è una chat.{" "}
                  <span className="text-brand-terra italic">Letteralmente.</span>
                </h2>
                <p className="mt-5 text-brand-inkSoft leading-relaxed max-w-md">
                  Niente dashboard da imparare, niente login da ricordare.{" "}
                  <b className="text-brand-ink">
                    Scrivi all'assistente come scriveresti a un dipendente
                  </b>
                  , e lui esegue: cambi le istruzioni, controlli chi ha chiamato, ricarichi il
                  credito. Un messaggio, e fatto.
                </p>
              </div>

              <div
                className="reveal bg-brand-bg border border-brand-line rounded-2xl p-6 flex flex-col gap-3"
                aria-label="Esempio di conversazione Telegram"
              >
                <Bubble side="you" time="17:02">
                  Chi ha chiamato oggi?
                </Bubble>
                <Bubble side="bot" time="17:02">
                  3 chiamate oggi: 2 prenotazioni confermate (Bianchi x4 sabato, Conte x2 domenica) e
                  1 richiesta info sul pranzo di Natale — ti ho lasciato il recapito.
                </Bubble>
                <Bubble side="you" time="17:03">
                  Da domani il menu degustazione costa 65€
                </Bubble>
                <Bubble side="bot" time="17:03">
                  Fatto ✓ Da domani comunico 65€ per il menu degustazione.
                </Bubble>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA → form di attivazione ────────────────────────────────── */}
        <section className="bg-brand-olive text-brand-bg text-center px-6 py-24 md:py-28">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-terra mb-5">
            Adesso tocca a te
          </p>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">
            Hai visto come funziona.
            <span className="block text-brand-terra italic">Ora attivala per te.</span>
          </h2>
          <p className="mt-5 text-brand-bg/75 max-w-lg mx-auto">
            Compili il form, ti prepariamo la tua 24Vox e la colleghi alla tua linea. Niente
            contratti.
          </p>
          <Link
            to="/attivazione"
            data-testid="cta-attivazione"
            className="inline-flex items-center gap-2 mt-9 bg-brand-terra text-white font-heading font-bold px-8 py-4 rounded-full text-base hover:-translate-y-0.5 transition-transform"
          >
            Attiva la tua 24Vox <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
          </Link>
          <p className="mt-5 text-sm text-brand-bg/55 max-w-xl mx-auto">
            588€/anno la piattaforma · chiamate a consumo, paghi solo i minuti reali · rimborso entro
            30 giorni
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/* ── Bolla chat Telegram ──────────────────────────────────────────────── */
function Bubble({ side, time, children }) {
  const you = side === "you";
  return (
    <div
      className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-snug ${
        you
          ? "self-end bg-brand-olive text-brand-bg rounded-br-sm"
          : "self-start bg-brand-bg2 text-brand-ink rounded-bl-sm"
      }`}
    >
      {children}
      <span className={`block text-[0.65rem] mt-1 text-right ${you ? "text-brand-bg/60" : "text-brand-inkSoft"}`}>
        {time}
      </span>
    </div>
  );
}
